import { AccessEventAction } from '@dealstack/shared/lib/backendClient';
import { asEnumValue } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogTabs.utils.ts';
import type {
	DealEventAccessTct,
	DealEventTct,
	SessionInfo,
} from '@dealstack/shared/lib/backendClient';
import { AuditAction } from '@dealstack/shared/lib/generated';
import { formatNumberToUI } from '@dealstack/shared/lib/format/number';
import { normalizeScreamingSnakeToUI } from '@dealstack/shared/lib/format/string';
import { normalizeCamelCaseToUI } from '@dealstack/shared/lib/format/normalizeCamelCaseToUI';
import { TransformedAuditData } from '@dealstack/frontend/shared/components/Audit/types.ts';
import {
	formatFieldName,
	formatValue,
} from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import { toAuditFieldValue } from '@dealstack/frontend/shared/components/Audit/AuditLayout/auditTableData.utils.ts';
import { formatRoles } from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';

type Translate = (key: string) => string;

const getFieldLabel = (key: string, t: Translate, tableName: string): string => {
	const mapped = formatFieldName(key, t, tableName);

	return mapped === key ? normalizeScreamingSnakeToUI(normalizeCamelCaseToUI(key)) : mapped;
};

const formatChangeValue = (value: unknown, key: string, tableName: string): string =>
	typeof value === 'number' && Number.isInteger(value)
		? formatNumberToUI(value)
		: formatValue(
				toAuditFieldValue(value),
				key,
				undefined,
				undefined,
				undefined,
				undefined,
				tableName
			);

export const getDealEventChangeAction = (event: DealEventTct): AuditAction => {
	// The feed already classifies an access row: GRANT reads as a create, REVOKE as a delete and
	// CHANGE as an update, which is the layout each one's detail needs.
	if (event.access) {
		// The two AuditAction declarations carry the same strings; this picks the enum member.
		return asEnumValue(Object.values(AuditAction), event.action) ?? AuditAction.CREATE;
	}

	if (event.oldData && event.newData) {
		return AuditAction.UPDATE;
	}

	return event.oldData ? AuditAction.DELETE : AuditAction.CREATE;
};

/**
 * An access row carries its change as roles rather than a recorded row diff. A grant has no
 * roles before and a revoke none after, so only a change reads as a before/after pair — the
 * other two use the created/deleted layout, which prints a single value.
 */
const getAccessChanges = (
	access: DealEventAccessTct,
	t: Translate,
	tRole: Translate
): TransformedAuditData[] => {
	const field = t('deal_event_log.detail.roles');

	if (access.action === AccessEventAction.GRANT) {
		return [
			{ id: 'roles', field, value: formatRoles(access.rolesAfter, tRole), type: 'created' },
		];
	}

	if (access.action === AccessEventAction.REVOKE) {
		return [
			{ id: 'roles', field, value: formatRoles(access.rolesBefore, tRole), type: 'deleted' },
		];
	}

	return [
		{
			id: 'roles',
			field,
			beforeValue: formatRoles(access.rolesBefore, tRole),
			afterValue: formatRoles(access.rolesAfter, tRole),
			isChanged: true,
			type: 'update',
		},
	];
};

export const getDealEventChanges = (
	event: DealEventTct,
	t: Translate,
	tRole: Translate
): TransformedAuditData[] => {
	if (event.access) {
		return getAccessChanges(event.access, t, tRole);
	}

	const tableName = event.entity.type;
	const action = getDealEventChangeAction(event);

	return event.changedFields.map((key): TransformedAuditData => {
		const oldValue = event.oldData?.[key];
		const newValue = event.newData?.[key];
		const field = getFieldLabel(key, t, tableName);

		if (action === AuditAction.CREATE) {
			return {
				id: key,
				field,
				value: formatChangeValue(newValue, key, tableName),
				type: 'created',
			};
		}

		if (action === AuditAction.DELETE) {
			return {
				id: key,
				field,
				value: formatChangeValue(oldValue, key, tableName),
				type: 'deleted',
			};
		}

		return {
			id: key,
			field,
			beforeValue: formatChangeValue(oldValue, key, tableName),
			afterValue: formatChangeValue(newValue, key, tableName),
			isChanged:
				oldValue !== undefined &&
				newValue !== undefined &&
				JSON.stringify(oldValue) !== JSON.stringify(newValue),
			type: 'update',
		};
	});
};

export const hasDealEventDetail = (event: DealEventTct, session?: SessionInfo): boolean =>
	event.changedFields.length > 0 || !!event.access || !!session;
